import { throttle } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { range } from 'utils/math';
import useMergedRefs from './useMergedRefs';

const DEFAULT_TIMEOUT_MILLIS = 100;

interface UseScrollOffsetOptions {
  timeoutMillis?: number;
}

interface PercentageState {
  x: number | undefined;
  y: number | undefined;
}

interface UseScrollOffsetResult<
  TScroll extends HTMLElement,
  TContent extends HTMLElement,
> {
  contentRef: React.Ref<TContent>;
  progress: PercentageState;
  calculateScrollPercentage: () => void;
  scrollRef: React.Ref<TScroll>;
}

export default function useScrollOffset<
  TScroll extends HTMLElement,
  TContent extends HTMLElement,
>(opts?: UseScrollOffsetOptions): UseScrollOffsetResult<TScroll, TContent> {
  const { timeoutMillis = DEFAULT_TIMEOUT_MILLIS } = opts ?? {};

  const scrollRef = useRef<TScroll>(null);
  const [progress, setProgress] = useState<PercentageState>({
    x: undefined,
    y: undefined,
  });

  const calculateScrollPercentage = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      const maxHScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const maxVScroll = Math.max(0, el.scrollHeight - el.clientHeight);

      setProgress({
        x: range(0, maxHScroll, 0, 1, el.scrollLeft),
        y: range(0, maxVScroll, 0, 1, el.scrollTop),
      });
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;

    const handleScroll = throttle(calculateScrollPercentage, timeoutMillis);

    if (el) {
      el.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, [calculateScrollPercentage, timeoutMillis]);

  const { ref: scrollResizeRef } = useResizeDetector<TScroll>({
    onResize: calculateScrollPercentage,
  });

  const { ref: contentRef } = useResizeDetector({
    onResize: calculateScrollPercentage,
  });

  const mergedScrollRefs = useMergedRefs([scrollRef, scrollResizeRef]);

  return {
    calculateScrollPercentage,
    contentRef,
    progress,
    scrollRef: mergedScrollRefs,
  };
}
